import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type AnalyticsView =
  | 'realtime'
  | 'overview'
  | 'events'
  | 'users'
  | 'catalog'
  | 'cleanup';

const ALL_VIEWS: AnalyticsView[] = ['realtime', 'overview', 'events', 'users', 'catalog', 'cleanup'];

export interface AnalyticsFilters {
  view: AnalyticsView;
  since?: string;
  until?: string;
  compare: boolean;
  preset?: DateRangePreset;
  eventName?: string;
  category?: string;
  customerId?: string;
  actorId?: string;
  screen?: string;
  actorType?: string;
  hideLegacy: boolean;
  excludeEvents: string[];
}

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'last_7d'
  | 'last_28d'
  | 'last_90d'
  | 'this_month'
  | 'custom';

export interface AnalyticsFilterUpdate {
  view?: AnalyticsView;
  since?: string | null;
  until?: string | null;
  compare?: boolean;
  preset?: DateRangePreset | null;
  eventName?: string | null;
  category?: string | null;
  customerId?: string | null;
  actorId?: string | null;
  screen?: string | null;
  actorType?: string | null;
  hideLegacy?: boolean;
  excludeEvents?: string[] | null;
}

const DEFAULT_VIEW: AnalyticsView = 'overview';

function parseView(raw: string | null): AnalyticsView {
  if (raw && (ALL_VIEWS as string[]).includes(raw)) return raw as AnalyticsView;
  return DEFAULT_VIEW;
}

function parsePreset(raw: string | null): DateRangePreset | undefined {
  const valid: DateRangePreset[] = ['today', 'yesterday', 'last_7d', 'last_28d', 'last_90d', 'this_month', 'custom'];
  if (raw && (valid as string[]).includes(raw)) return raw as DateRangePreset;
  return undefined;
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

export function useAnalyticsFilters(): {
  filters: AnalyticsFilters;
  setFilters: (update: AnalyticsFilterUpdate) => void;
  resetFilters: () => void;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<AnalyticsFilters>(() => ({
    view: parseView(params.get('view')),
    since: params.get('since') || undefined,
    until: params.get('until') || undefined,
    compare: params.get('compare') === '1',
    preset: parsePreset(params.get('preset')),
    eventName: params.get('eventName') || undefined,
    category: params.get('category') || undefined,
    customerId: params.get('customerId') || undefined,
    actorId: params.get('actorId') || undefined,
    screen: params.get('screen') || undefined,
    actorType: params.get('actorType') || undefined,
    hideLegacy: params.get('hideLegacy') !== '0',
    excludeEvents: parseList(params.get('excludeEvents')),
  }), [params]);

  const setFilters = useCallback((update: AnalyticsFilterUpdate) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      const set = (key: string, value: string | null | undefined) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, value);
      };
      if (update.view !== undefined) set('view', update.view);
      if (update.since !== undefined) set('since', update.since);
      if (update.until !== undefined) set('until', update.until);
      if (update.compare !== undefined) set('compare', update.compare ? '1' : null);
      if (update.preset !== undefined) set('preset', update.preset);
      if (update.eventName !== undefined) set('eventName', update.eventName);
      if (update.category !== undefined) set('category', update.category);
      if (update.customerId !== undefined) set('customerId', update.customerId);
      if (update.actorId !== undefined) set('actorId', update.actorId);
      if (update.screen !== undefined) set('screen', update.screen);
      if (update.actorType !== undefined) set('actorType', update.actorType);
      if (update.hideLegacy !== undefined) set('hideLegacy', update.hideLegacy ? null : '0');
      if (update.excludeEvents !== undefined) {
        const list = update.excludeEvents;
        set('excludeEvents', list && list.length > 0 ? list.join(',') : null);
      }
      return next;
    }, { replace: false });
  }, [setParams]);

  const resetFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: false });
  }, [setParams]);

  return { filters, setFilters, resetFilters };
}

/** Compute since/until ISO date strings (YYYY-MM-DD) for a preset, anchored to now. */
export function rangeForPreset(preset: DateRangePreset, now: Date = new Date()): { since: string; until: string } | null {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayStr = fmt(today);

  switch (preset) {
    case 'today':
      return { since: todayStr, until: todayStr };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { since: fmt(y), until: fmt(y) };
    }
    case 'last_7d': {
      const s = new Date(today);
      s.setDate(s.getDate() - 6);
      return { since: fmt(s), until: todayStr };
    }
    case 'last_28d': {
      const s = new Date(today);
      s.setDate(s.getDate() - 27);
      return { since: fmt(s), until: todayStr };
    }
    case 'last_90d': {
      const s = new Date(today);
      s.setDate(s.getDate() - 89);
      return { since: fmt(s), until: todayStr };
    }
    case 'this_month': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { since: fmt(s), until: todayStr };
    }
    case 'custom':
    default:
      return null;
  }
}

/** For a given date range, return the immediately-preceding range of equal length. */
export function previousPeriod(since: string, until: string): { since: string; until: string } | null {
  const s = new Date(since + 'T00:00:00Z');
  const u = new Date(until + 'T00:00:00Z');
  if (Number.isNaN(s.getTime()) || Number.isNaN(u.getTime()) || s > u) return null;
  const lengthDays = Math.round((u.getTime() - s.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  const prevUntil = new Date(s);
  prevUntil.setUTCDate(prevUntil.getUTCDate() - 1);
  const prevSince = new Date(prevUntil);
  prevSince.setUTCDate(prevSince.getUTCDate() - (lengthDays - 1));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { since: fmt(prevSince), until: fmt(prevUntil) };
}
